// @/components/announcements/AnnouncementHost.jsx

/**
 * AnnouncementHost
 *
 * Always-mounted controller for the per-user announcement system. Lives at
 * app shell (gatsby-browser.js wrapPageElement). Defers paint while the
 * TermsAcceptanceGate is open via window.__termsGateOpen — legal first,
 * marketing second.
 *
 * Display queue: one announcement at a time, sorted by startsAt ASC. After
 * user actions on the current one, the next in the queue fades in.
 * Refetches on visibilitychange (5-min throttled in the handler).
 *
 * Cross-tab dismiss: listens for storage events on `announcement_state_*`
 * keys and removes the affected announcement from its queue without flashing.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLanguage } from '@/components/providers/LanguageContext';
import { authService } from '@/services/auth';
import AnnouncementDialog from './AnnouncementDialog';

// Preload an announcement's image (when present) before the dialog
// opens so we don't see the dialog grow as the image lands. Falls
// through (resolves) on load failure or after a 2s timeout —
// whichever comes first — so a slow CDN never blocks the welcome
// permanently.
const IMAGE_PRELOAD_TIMEOUT_MS = 2000;

function preloadImage(url) {
  if (!url || typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const finish = () => { if (!settled) { settled = true; resolve(); } };
    img.onload = finish;
    img.onerror = finish;
    img.src = url;
    setTimeout(finish, IMAGE_PRELOAD_TIMEOUT_MS);
  });
}

const AnnouncementHost = () => {
  const { locale } = useLanguage?.() || { locale: 'en' };
  const [queue, setQueue] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inFlightRef = useRef(false);

  const handler = typeof window !== 'undefined' ? window.appStore?.announcementsHandler : null;

  const refresh = useCallback(async () => {
    if (!handler) return;
    const auth = authService.getAuthState?.();
    if (!auth?.authenticated) return;
    if (typeof window !== 'undefined' && window.__termsGateOpen) {
      console.log('[AnnouncementHost] defer refresh — terms gate is open');
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const items = await handler.fetchActive(locale);
      // Preload the FIRST item's image (if any) before paint so the
      // dialog opens at its final size. Subsequent items in the queue
      // get preloaded as the user advances through them.
      if (items.length && items[0].imageUrl) {
        await preloadImage(items[0].imageUrl);
      }
      setQueue(items);
      setActiveIndex(0);
    } finally {
      inFlightRef.current = false;
    }
  }, [handler, locale]);

  // When the terms gate closes, re-run the visibility check so the
  // announcement appears immediately (instead of waiting for the next
  // visibilitychange tick). The gate dispatches `termsGateClosed`
  // synchronously inside its useEffect.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onGateClosed = () => {
      console.log('[AnnouncementHost] terms gate closed — refreshing');
      refresh();
    };
    window.addEventListener('termsGateClosed', onGateClosed);
    return () => window.removeEventListener('termsGateClosed', onGateClosed);
  }, [refresh]);

  useEffect(() => {
    refresh();
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refresh();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [refresh]);

  // Cross-tab sync: another tab dismissed/clicked → drop from local queue
  useEffect(() => {
    if (!handler) return;
    const onStorage = (e) => {
      const key = handler.parseStorageEvent(e);
      if (!key) return;
      setQueue((prev) => prev.filter((item) => item.key !== key));
      setActiveIndex(0);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
      }
    };
  }, [handler]);

  // Stacking guard: never paint the announcement while the terms gate
  // is open. Without this, the announcement dialog mounts in the DOM
  // behind the gate's backdrop and click events hit the wrong layer.
  // The `termsGateClosed` event listener above re-runs refresh() the
  // moment the gate closes, so the announcement appears immediately.
  const isTermsGateOpen = typeof window !== 'undefined' && window.__termsGateOpen === true;
  const current = !isTermsGateOpen ? (queue[activeIndex] || null) : null;

  // Mirror of the terms gate's __termsGateOpen flag, for the CookieBanner.
  // A non-dismissible announcement (Headless Dialog with a no-op onClose)
  // swallows every click outside its panel, so the banner — which paints
  // above the backdrop at z-50 — looks clickable but is dead. The banner
  // hides itself while either blocking dialog is open (see cookie-banner.jsx)
  // and `blockingOverlayChanged` tells it when to re-check.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__announcementOpen = !!current;
    window.dispatchEvent(new CustomEvent('blockingOverlayChanged'));
    return () => {
      window.__announcementOpen = false;
      window.dispatchEvent(new CustomEvent('blockingOverlayChanged'));
    };
  }, [current]);

  const handleAction = useCallback(
    async (action) => {
      if (!current || !handler) return;
      await handler.track(current.key, action);
      // Move to next item; if queue exhausted, close.
      const nextQueue = queue.filter((_, i) => i !== activeIndex);
      // Preload the next item's image before flipping state so the
      // next dialog opens at its final size too (no jump between
      // sequential announcements).
      if (nextQueue.length && nextQueue[0].imageUrl) {
        await preloadImage(nextQueue[0].imageUrl);
      }
      setQueue(nextQueue);
      setActiveIndex(0);
    },
    [current, handler, activeIndex, queue]
  );

  if (!current) return null;

  return (
    <AnnouncementDialog
      isOpen={!!current}
      announcement={current}
      onAction={handleAction}
    />
  );
};

export default AnnouncementHost;
